// FORGED Ed Systems — DEPLOY004d — v0.4.4 — ResetPasswordPage.tsx
// Last touched: 2026-05-20
//
// /reset-password — teacher lands here after clicking the password-recovery
// link in their email. Supabase auto-detects the recovery token from the URL
// hash and emits a PASSWORD_RECOVERY auth event, which puts us in a state
// where the user can call supabase.auth.updateUser({ password }) without
// being fully signed in.

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "./supabaseClient";

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [recoveryReady, setRecoveryReady] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  // Listen for the PASSWORD_RECOVERY event Supabase emits when the user
  // arrives via a recovery link. The page also handles the case where the
  // event already fired before this component mounted (rare but possible
  // if the URL is reloaded).
  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setRecoveryReady(true);
      }
      // If there's already an authenticated session here and we didn't get
      // the PASSWORD_RECOVERY event (e.g. user navigated here while signed
      // in), still allow them to change their password.
      if (session && !recoveryReady) {
        setRecoveryReady(true);
      }
    });

    // Also check current session synchronously in case the auth-state
    // listener fired before we subscribed.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        setRecoveryReady(true);
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (password.length < 12) {
      setErrorMsg("Please use at least 12 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    const res = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (res.error) {
      setErrorMsg(res.error.message);
      return;
    }

    setDone(true);
    // After a successful change, sign the user out so they re-authenticate
    // with the new password. Avoids leaving a half-authenticated session in
    // recovery mode.
    setTimeout(async () => {
      await supabase.auth.signOut();
      navigate("/sign-in");
    }, 2000);
  }

  if (done) {
    return (
      <main className="container">
        <header className="brand-header">
          <h1>Password updated</h1>
          <p className="tagline">
            Redirecting you to sign in with your new password…
          </p>
        </header>
      </main>
    );
  }

  if (!recoveryReady) {
    return (
      <main className="container">
        <header className="brand-header">
          <h1>Reset password</h1>
          <p className="tagline">Verifying your recovery link…</p>
        </header>
        <section className="hero">
          <div className="info-card">
            <p style={{ marginTop: 0 }}>
              If this page doesn't update within a few seconds, the recovery
              link may be expired or invalid. Request a new one below.
            </p>
            <div className="cta-row" style={{ marginTop: "1rem" }}>
              <Link to="/forgot-password" className="cta secondary">
                Request a new reset link
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="container">
      <header className="brand-header">
        <h1>Set a new password</h1>
        <p className="tagline">Choose something memorable — 12+ characters.</p>
      </header>

      <form onSubmit={handleSubmit} className="form-stack" style={{ maxWidth: "440px" }}>
        <label className="field">
          <span>New password</span>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
            autoFocus
          />
          <small className="field-hint">Minimum 12 characters.</small>
        </label>

        <label className="field">
          <span>Confirm new password</span>
          <input
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            minLength={12}
          />
        </label>

        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        <button type="submit" className="cta primary" disabled={submitting}>
          {submitting ? "Updating…" : "Update password"}
        </button>
      </form>
    </main>
  );
}
